import { useEffect, useState } from "react";
import Rating from "@/components/base/Rating";
import ReviewForm from "./ReviewForm";
import type { Review } from "@/types";

interface BusinessReviewsProps {
  businessId: string;
  businessName: string;
  reviews: Review[];
}

const isPublished = (r: Review) => !r.status || r.status === "published";

export default function BusinessReviews({ businessId, businessName, reviews }: BusinessReviewsProps) {
  const [list, setList] = useState<Review[]>(reviews);
  const [visible, setVisible] = useState(3);

  useEffect(() => {
    setList(reviews);
  }, [reviews]);

  const published = list.filter(isPublished);
  const avg = published.length ? published.reduce((s, r) => s + r.rating, 0) / published.length : 0;

  const distribution = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: published.filter((r) => Math.round(r.rating) === star).length,
  }));

  const shown = list.slice(0, visible);

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-lg font-extrabold text-foreground-950">Ratings &amp; reviews</h2>
        <ReviewForm
          businessId={businessId}
          businessName={businessName}
          onSubmitted={(review) => setList((prev) => [review, ...prev])}
        />
      </div>

      {published.length > 0 ? (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6">
          <div className="text-center md:text-left">
            <div className="font-heading text-4xl font-extrabold text-foreground-950">{avg.toFixed(1)}</div>
            <div className="mt-1 flex justify-center md:justify-start">
              <Rating value={avg} />
            </div>
            <p className="mt-1 text-xs text-foreground-500">Based on {published.length} reviews</p>
          </div>
          <div className="space-y-2">
            {distribution.map((d) => {
              const pct = published.length ? Math.round((d.count / published.length) * 100) : 0;
              return (
                <div key={d.star} className="flex items-center gap-3 text-xs text-foreground-600">
                  <span className="inline-flex w-10 items-center gap-1">
                    {d.star} <i className="ri-star-fill text-primary-500"></i>
                  </span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-background-200">
                    <div className="h-full rounded-full bg-primary-500" style={{ width: `${pct}%` }}></div>
                  </div>
                  <span className="w-8 text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="mt-4 text-sm text-foreground-600">
          No reviews yet — be the first to share your experience with {businessName}.
        </p>
      )}

      {list.length > 0 && (
        <div className="mt-6 divide-y divide-background-100">
          {shown.map((r) => {
            const pending = r.status && r.status !== "published";
            return (
              <article key={r.id} className="py-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-bold text-secondary-900">
                    {r.authorName.charAt(0).toUpperCase()}
                  </span>
                  <div>
                    <span className="block text-sm font-bold text-foreground-950">{r.authorName}</span>
                    <span className="block text-[11px] text-foreground-500">
                      {new Date(r.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  </div>
                  {pending ? (
                    <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-[11px] font-bold text-primary-900">
                      <i className="ri-time-line"></i> Pending approval
                    </span>
                  ) : (
                    <span className="ml-auto">
                      <Rating value={r.rating} />
                    </span>
                  )}
                </div>
                <p className="mt-3 text-sm text-foreground-700 leading-relaxed">{r.comment}</p>
                {r.ownerReply && (
                  <div className="mt-3 rounded-md border-l-2 border-primary-400 bg-primary-50 px-4 py-3">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-primary-800">Owner reply</p>
                    <p className="mt-1 text-sm text-foreground-700">{r.ownerReply}</p>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {visible < list.length && (
        <button
          type="button"
          onClick={() => setVisible(list.length)}
          className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 text-sm font-semibold text-foreground-800 hover:border-primary-400 whitespace-nowrap cursor-pointer"
        >
          Show all {list.length} reviews <i className="ri-arrow-down-s-line"></i>
        </button>
      )}
    </section>
  );
}